

















































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import Mapbox from '@/components/organisms/Mapbox.vue';
import NewcrossLoader from '@/components/atoms/NewcrossLoader.vue';
import NewcrossButton from '@/components/atoms/NewcrossButton.vue';
import FAB from '@/components/atoms/FAB.vue';
import { LngLatBounds } from 'mapbox-gl';
import { Building, Catalogue } from '@/types';
import getHashParams from '@/lib/getHashParams';
import Store from '@/store';
import HealthForceGoApp from '@/lib/HealthForceGoApp';
import checkIfValidBrowser from '@/lib/checkIfValidBrowser';
import calculateFidelityLevel from '@/lib/calculateFidelityLevel';
import ExternalPagePlugin from '@/plugins/ExternalPagePlugin';
import HintOverlay from '@/components/molecules/HintOverlay.vue';

@Component({
  components: {
    Mapbox,
    NewcrossLoader,
    NewcrossButton,
    FAB,
    HintOverlay,
  },
  beforeRouteEnter: (_to, _from, next) => {
    const validBrowser = checkIfValidBrowser();
    if (!validBrowser) {
      next({
        name: 'Invalid Browser',
      });
    } else {
      const fidelityLevel = calculateFidelityLevel();
      Store.dispatch('SET_FIDELITY_LEVEL', fidelityLevel);
      Store.dispatch('SET_LOADING_OVERLAY', 'Logging in');
      const { jwt } = getHashParams();
      Store.dispatch('INITIALISE', jwt)
        .then(() => Store.dispatch('SET_LOADING_OVERLAY', 'Loading learning'))
        .then(() => Store.dispatch('buildings/GET_BUILDING_DATA'))
        .then(() => Store.dispatch('SET_LOADING_OVERLAY', 'Loading skills'))
        .then(() => Store.dispatch('skills/GET_SKILLS_DATA'))
        .then(() => Store.dispatch('SET_LOADING_OVERLAY', 'Loading music'))
        .then(() => Store.dispatch('audio/LOAD_MUSIC'))
        .then(() => Store.dispatch('SET_LOADING_OVERLAY', 'Loading map'))
        .then(() => {
          next();
        })
        .catch((e) => {
          console.error(e);
          next({
            name: 'Bad Request',
          });
        });
    }
  },
})
export default class World extends Vue {
  private get isAudible() {
    return !this.$store.state.audio.muted;
  }

  private mapLoaded = false;

  private spritesLoaded = false;

  private showIntroButton = false;

  private hideClouds = true;

  private showQuickNavigationFAB = false;

  private get showingQuickNavigation(): boolean {
    return this.$route.path.includes('quick-navigation');
  }

  private get showingLearingPlan(): boolean {
    return this.$route.path.includes('learning-plan');
  }

  private showFirstTimeHint = false;

  private showingExternalPage = false;

  private beforeMount() {
    this.showingExternalPage = ExternalPagePlugin.externalPages.length > 0;
    if (ExternalPagePlugin.EventBus) {
      ExternalPagePlugin.EventBus.$on('externalPages', () => {
        this.showingExternalPage = ExternalPagePlugin.externalPages.length > 0;
      });
    }
  }

  private get activeBuildingId(): string {
    return this.$route.params.buildingId;
  }

  // When the route changes, watch for the building ID and pan the
  // mapbox map to that location, or exit if leaving a building
  @Watch('activeBuildingId') private onActiveBuildingIdChanged(
    activeBuildingId: string
  ) {
    if (activeBuildingId) {
      this.panMapToBuildingUrlRoute(true);
      this.pauseMusic();
    } else {
      (this.$refs.mapbox as Mapbox).exitLocation();
      this.playMusic();
    }
  }

  private mounted() {
    if (this.activeBuildingId) {
      // On startup, if there is a building then set the map location there
      (this.$refs.mapbox as Mapbox).startMapContent();
      this.showQuickNavigationFAB = true;
      this.panMapToBuildingUrlRoute(false);
    } else if (this.$route.path === '/') {
      // On startup, if the user is on the root path its the intro state
      this.showIntroButton = true;
      this.hideClouds = false;
    } else {
      // On startup, if its not on a building or route then the user is in another menu
      // So just zoom the user in to the started state by default in the background
      (this.$refs.mapbox as Mapbox).doMapFlyInTransition(false);
      (this.$refs.mapbox as Mapbox).startMapContent();
      this.showQuickNavigationFAB = true;
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private beforeDestroy() {
    this.pauseMusic();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange() {
    if (document.hidden) return;
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading');
    this.$store.dispatch('REFRESH_DOCEBO_TOKEN').then(
      () => {
        this.$store.dispatch('SET_LOADING_OVERLAY', false);
      },
      () => {
        this.$router
          .push({
            name: 'Unauthorised',
          })
          .catch((e) => {
            console.assert(true, e);
          });
      }
    );
  }

  private onMapLoaded() {
    this.mapLoaded = true;
    this.$store.dispatch('SET_LOADING_OVERLAY', 'Loading animations');
    this.checkIfAllLoaded();
  }

  private onSpritesLoaded() {
    this.spritesLoaded = true;
    this.checkIfAllLoaded();
  }

  private checkIfAllLoaded() {
    if (this.mapLoaded && this.spritesLoaded) {
      this.$store.dispatch('SET_LOADING_OVERLAY', false);
    }
  }

  private panMapToBuildingUrlRoute(animated: boolean) {
    const building = this.$store.state.config.buildings.find(
      (b: Building) => b.id === this.activeBuildingId
    );
    const bounds = new LngLatBounds(building.buildingBounds);
    (this.$refs.mapbox as Mapbox).enterLocation(bounds, animated);
  }

  public onIntroButtonPressed() {
    if (!this.showIntroButton) return;
    this.showIntroButton = false;
    this.hideClouds = true;
    this.playMusic();
    (this.$refs.mapbox as Mapbox).doMapFlyInTransition(true).then(() => {
      (this.$refs.mapbox as Mapbox).startMapContent();
      this.showQuickNavigationFAB = true;
      if (localStorage.getItem('nxw-visited') !== 'true') {
        this.showFirstTimeHint = true;
        localStorage.setItem('nxw-visited', 'true');
      }
    });
  }

  private onHintPressed() {
    this.showFirstTimeHint = false;
  }

  private onBuildingPressed(building: Building) {
    const catalogues: Catalogue[] = this.$store.getters[
      'catalogues/getConditionalCataloguesForBuilding'
    ](building.id);
    this.$router
      .push({
        name: 'Building',
        params: {
          buildingId: building.id,
          catalogueId: catalogues[0].doceboCatalogueId.toString(),
        },
      })
      .catch((e) => {
        console.assert(true, e);
      });
  }

  private onQuickNavigationPressed() {
    this.pauseMusic();
    this.$router
      .push({
        name: 'Quick Navigation',
      })
      .catch((e) => {
        console.assert(true, e);
      });
  }

  private onAudioToggleButtonPressed() {
    const newAudibleState = !this.isAudible;
    this.$store.dispatch('audio/SET_AUDABILITY', newAudibleState);
  }

  private closeModal() {
    this.playMusic();
    this.$router
      .push({
        name: 'World',
      })
      .catch((e) => {
        console.assert(true, e);
      });
  }

  private onCloseLearningPlanModal() {
    this.$router
      .push({
        name: 'Building',
      })
      .catch((e) => {
        console.assert(true, e);
      });
  }

  private playMusic() {
    this.$store.dispatch('audio/PLAY_MUSIC');
  }

  private pauseMusic() {
    this.$store.dispatch('audio/PAUSE_MUSIC');
  }

  // eslint-disable-next-line class-methods-use-this
  private onExitPressed() {
    HealthForceGoApp.dismiss();
  }
}
